import { ZoneType, SessionDiscipline, SessionTargets, AthleteSettings, RunPaceZone, SwimZone, BikePowerZone, HRZone } from '../../types/athlete';
import { calculateRunPaces, calculateSwimZones, calculateBikePowerZones } from '../zones/zoneCalculations';

interface ZoneLookupResult {
  pace?: string;
  power?: string;
  heartRate?: string;
  description: string;
}

const ZONE_TYPE_MAP: Record<ZoneType, { name: string; multiplier: number }> = {
  'E': { name: 'Easy', multiplier: 1.25 },
  'L': { name: 'Long', multiplier: 1.15 },
  'M': { name: 'Marathon', multiplier: 1.05 },
  'T': { name: 'Threshold', multiplier: 1.00 },
  'I': { name: 'Interval', multiplier: 0.95 },
  'Z1': { name: 'Recovery', multiplier: 0.55 },
  'Z2': { name: 'Endurance', multiplier: 0.75 },
  'Z3': { name: 'Tempo', multiplier: 0.90 },
  'Z4': { name: 'Threshold', multiplier: 1.05 },
  'Z5': { name: 'VO2max', multiplier: 1.20 },
  'Z6': { name: 'Anaerobic', multiplier: 1.50 },
  'Z7': { name: 'Neuromuscular', multiplier: 2.00 },
  'CSS': { name: 'CSS', multiplier: 1.00 },
  'CSS+X': { name: 'CSS + X sec', multiplier: 1.08 },
  'CSS-X': { name: 'CSS - X sec', multiplier: 0.92 },
  'none': { name: 'N/A', multiplier: 1.00 },
};

const CSS_OFFSETS: Record<string, number> = {
  'CSS+12': 12,
  'CSS+10': 10,
  'CSS+8': 8,
  'CSS+6': 6,
  'CSS+4': 4,
  'CSS+2': 2,
  'CSS': 0,
  'CSS-2': -2,
  'CSS-4': -4,
  'CSS-6': -6,
};


export const getRunPaceForZone = (
  zoneType: ZoneType,
  runPaces: RunPaceZone[]
): ZoneLookupResult | null => {
  const zoneConfig = ZONE_TYPE_MAP[zoneType];
  if (!zoneConfig) return null;

  const paceMap: Record<string, string> = {
    'E': runPaces[0]?.perKm || '',
    'L': runPaces[1]?.perKm || '',
    'M': runPaces[2]?.perKm || '',
    'T': runPaces[3]?.perKm || '',
    'I': runPaces[4]?.perKm || '',
  };

  const pace = paceMap[zoneType];
  if (!pace) return null;

  return {
    pace,
    description: zoneConfig.name,
  };
};

export const getSwimPaceForZone = (
  zoneType: ZoneType,
  swimZones: SwimZone[]
): ZoneLookupResult | null => {
  let targetZone: SwimZone | undefined;

  if (zoneType === 'CSS') {
    targetZone = swimZones[2];
  } else if (CSS_OFFSETS[zoneType] !== undefined) {
    const offset = CSS_OFFSETS[zoneType];
    targetZone = swimZones.find(z => z.zone === 3) || swimZones[2];
    const basePaceSeconds = parsePaceToSeconds(targetZone.pacePer100m);
    const adjustedSeconds = basePaceSeconds + offset;
    const adjustedPace = formatPace(adjustedSeconds);
    return {
      pace: adjustedPace,
      description: `${zoneType} pace`,
    };
  }

  if (!targetZone) return null;

  return {
    pace: targetZone.pacePer100m,
    description: targetZone.name,
  };
};

export const getBikePowerForZone = (
  zoneType: ZoneType,
  bikeZones: BikePowerZone[]
): ZoneLookupResult | null => {
  const zoneNum = parseInt(zoneType.replace('Z', ''));
  if (isNaN(zoneNum)) return null;

  const targetZone = bikeZones.find(z => z.zone === zoneNum);
  if (!targetZone) return null;

  return {
    power: targetZone.watts,
    description: targetZone.name,
  };
};

export const getHRForZone = (
  zoneNum: number,
  hrZones: HRZone[]
): ZoneLookupResult | null => {
  const targetZone = hrZones.find(z => z.zone === zoneNum);
  if (!targetZone) return null;

  return {
    heartRate: targetZone.range,
    description: targetZone.name,
  };
};

export const calculateSessionTargets = (
  discipline: SessionDiscipline,
  zoneType: ZoneType,
  zoneValue: number,
  athleteSettings: AthleteSettings
): SessionTargets => {
  const runPaces = athleteSettings.run_reference_distance && athleteSettings.run_reference_time
    ? calculateRunPaces(athleteSettings.run_reference_distance, athleteSettings.run_reference_time)
    : [];

  const swimZones = athleteSettings.css_value
    ? calculateSwimZones(athleteSettings.css_value)
    : [];

  const bikeZones = athleteSettings.cycling_ftp
    ? calculateBikePowerZones(athleteSettings.cycling_ftp)
    : [];


  let primary: SessionTargets['primary'] = {};
  let secondary: SessionTargets['secondary'] | undefined;
  let description = '';

  switch (discipline) {
    case 'run': {
      const result = getRunPaceForZone(zoneType, runPaces);
      if (result) {
        primary.pace = result.pace;
        description = `Run at ${result.description} pace`;
      }
      break;
    }
    case 'swim': {
      const result = getSwimPaceForZone(zoneType, swimZones);
      if (result) {
        primary.pace = result.pace;
        description = `Swim at ${result.description} pace`;
      }
      break;
    }
    case 'bike': {
      const result = getBikePowerForZone(zoneType, bikeZones);
      if (result) {
        primary.power = result.power;
        description = `Bike at ${result.description} intensity`;
      }
      break;
    }
    case 'strength':
      description = 'Strength training session';
      break;
  }

  return {
    primary,
    secondary,
    description,
  };
};

export const getZoneDescription = (zoneType: ZoneType, discipline: SessionDiscipline): string => {
  const descriptions: Record<string, string> = {
    'E': 'Easy, conversational pace',
    'L': 'Long run pace, sustained effort',
    'M': 'Marathon race pace',
    'T': 'Threshold pace, comfortably hard',
    'I': 'Interval/VO2max pace, very hard',
    'Z1': 'Active recovery, very easy spinning',
    'Z2': 'Endurance pace, sustainable all day',
    'Z3': 'Tempo, harder but sustainable',
    'Z4': 'Threshold, ~60 min max effort',
    'Z5': 'VO2max, high intensity short efforts',
    'Z6': 'Anaerobic, very hard short duration',
    'Z7': 'Neuromuscular, all-out sprint',
    'CSS': 'Critical Swim Speed pace',
    'CSS+12': 'Recovery swim pace (12 sec/100m slower)',
    'CSS+8': 'Easy swim pace (8 sec/100m slower)',
    'CSS+4': 'Tempo swim pace (4 sec/100m slower)',
    'CSS-4': 'Strong swim pace (4 sec/100m faster)',
  };

  return descriptions[zoneType] || `${zoneType} zone`;
};

const parsePaceToSeconds = (paceStr: string): number => {
  const parts = paceStr.split(':').map(Number);
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  return 0;
};

const formatPace = (secondsPer100m: number): string => {
  const mins = Math.floor(secondsPer100m / 60);
  const secs = Math.round(secondsPer100m % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const formatZoneTarget = (
  discipline: SessionDiscipline,
  zoneType: ZoneType,
  zoneValue: number,
  athleteSettings: AthleteSettings
): string => {
  const targets = calculateSessionTargets(discipline, zoneType, zoneValue, athleteSettings);
  
  if (discipline === 'strength') return 'Strength training';
  
  const parts: string[] = [];
  if (targets.primary.pace) parts.push(targets.primary.pace + '/km');
  if (targets.primary.power) parts.push(targets.primary.power);
  if (targets.primary.heartRate) parts.push(targets.primary.heartRate + ' bpm');
  
  return parts.length > 0 ? parts.join(' or ') : 'See zones';
};
